import { useCallback, useEffect, useMemo, useState } from 'react';
import { type SortingState } from '@tanstack/react-table';
import Input from '../common/Input';
import Modal from '../common/Modal';
import Table from '../common/Table';
import { createUserColumns } from './dashboardColumns';
import { User } from '../types';
import { useDeleteAllUsersMutation, useDeleteUserMutation, useUpdateUserMutation, useUsersQuery } from '../hooks/useUsers';

type UsersTabProps = {
  importData?: User[];
  importErrorMessage?: string;
};

function UsersTab({ importData, importErrorMessage = '' }: UsersTabProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [sorting, setSorting] = useState<SortingState>([{ id: 'customerName', desc: false }]);
  const [usersPageIndex, setUsersPageIndex] = useState(0);
  const [usersPageSize, setUsersPageSize] = useState(10);
  const [userActionMessage, setUserActionMessage] = useState('');
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deletingUser, setDeletingUser] = useState<User | null>(null);
  const [isDeleteAllModalOpen, setIsDeleteAllModalOpen] = useState(false);
  const [editUserFirstName, setEditUserFirstName] = useState('');
  const [editUserLastName, setEditUserLastName] = useState('');
  const [editUserPhoneNumber, setEditUserPhoneNumber] = useState('');

  const hasImportedUsers = Boolean(importData);
  const activeSort = sorting[0];
  const rawUsersSortField = activeSort?.id ? String(activeSort.id) : '';
  const usersSortField = rawUsersSortField === 'customerName' ? 'firstName' : rawUsersSortField;
  const usersSortDirection: 'asc' | 'desc' = activeSort?.desc ? 'desc' : 'asc';
  const shouldFetchUsers = !hasImportedUsers;
  const usersQuery = useUsersQuery(
    shouldFetchUsers,
    usersPageIndex + 1,
    usersPageSize,
    hasImportedUsers ? '' : debouncedSearchTerm,
    hasImportedUsers ? '' : usersSortField,
    usersSortDirection
  );
  const updateUserMutation = useUpdateUserMutation();
  const deleteUserMutation = useDeleteUserMutation();
  const deleteAllUsersMutation = useDeleteAllUsersMutation();

  const usersMeta = usersQuery.data?.meta;

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setUsersPageIndex(0);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    setUsersPageIndex(0);
  }, [usersSortField, usersSortDirection]);

  const sortedUsers = useMemo<User[]>(() => {
    const users = importData ?? usersQuery.data?.data ?? [];
    const query = searchTerm.trim().toLowerCase();
    const filteredUsers =
      !hasImportedUsers || query.length === 0
        ? users
        : users.filter((user) => {
            const customerName = `${user.firstName} ${user.lastName}`.trim().toLowerCase();
            const phoneNumber = user.phoneNumber.toLowerCase();
            const email = user.email.toLowerCase();
            const authorizeNetId = user.authorizeNetCustomerId.toLowerCase();
            return (
              customerName.includes(query) ||
              phoneNumber.includes(query) ||
              email.includes(query) ||
              authorizeNetId.includes(query)
            );
          });

    if (!hasImportedUsers) return filteredUsers;

    const copiedUsers = [...filteredUsers];
    const currentSort = sorting[0];
    if (!currentSort) return copiedUsers;

    const direction = currentSort.desc ? -1 : 1;
    copiedUsers.sort((a, b) => {
      const sortBy = String(currentSort.id);
      let result = 0;

      if (sortBy === 'customerName') {
        result = a.firstName.localeCompare(b.firstName);
      } else if (sortBy === 'lastName') {
        result = a.lastName.localeCompare(b.lastName);
      } else if (sortBy === 'email') {
        result = a.email.localeCompare(b.email);
      } else if (sortBy === 'phoneNumber') {
        result = a.phoneNumber.localeCompare(b.phoneNumber);
      } else if (sortBy === 'authorizeNetCustomerId') {
        result = a.authorizeNetCustomerId.localeCompare(b.authorizeNetCustomerId);
      } else {
        result = a.firstName.localeCompare(b.firstName);
      }

      return result * direction;
    });

    return copiedUsers;
  }, [hasImportedUsers, importData, searchTerm, sorting, usersQuery.data?.data]);

  const isUserEditChanged = useMemo(() => {
    if (!editingUser) return false;
    return (
      editUserFirstName.trim() !== editingUser.firstName.trim() ||
      editUserLastName.trim() !== editingUser.lastName.trim() ||
      editUserPhoneNumber.trim() !== editingUser.phoneNumber.trim()
    );
  }, [editUserFirstName, editUserLastName, editUserPhoneNumber, editingUser]);

  const handleCloseEditUserModal = useCallback(() => {
    setEditingUser(null);
    setEditUserFirstName('');
    setEditUserLastName('');
    setEditUserPhoneNumber('');
  }, []);

  const handleEditUser = useCallback((user: User) => {
    setUserActionMessage('');
    setEditingUser(user);
    setEditUserFirstName(user.firstName);
    setEditUserLastName(user.lastName);
    setEditUserPhoneNumber(user.phoneNumber);
  }, []);

  const handleDeleteUser = useCallback((user: User) => {
    setUserActionMessage('');
    setDeletingUser(user);
  }, []);

  const handleSaveUserEdit = useCallback(async () => {
    if (!editingUser || !isUserEditChanged) return;

    setUserActionMessage('');
    try {
      await updateUserMutation.mutateAsync({
        id: editingUser.id,
        firstName: editUserFirstName.trim(),
        lastName: editUserLastName.trim(),
        phoneNumber: editUserPhoneNumber.trim(),
      });
      setUserActionMessage(`User ${editingUser.firstName} ${editingUser.lastName} updated successfully.`);
      handleCloseEditUserModal();
      await usersQuery.refetch();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update user.';
      setUserActionMessage(message);
    }
  }, [editUserFirstName, editUserLastName, editUserPhoneNumber, editingUser, handleCloseEditUserModal, isUserEditChanged, updateUserMutation, usersQuery]);

  const handleConfirmDeleteUser = useCallback(async () => {
    if (!deletingUser) return;

    setUserActionMessage('');
    try {
      await deleteUserMutation.mutateAsync(deletingUser.id);
      setUserActionMessage(`User ${deletingUser.firstName} ${deletingUser.lastName} deleted successfully.`);
      setDeletingUser(null);
      await usersQuery.refetch();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete user.';
      setUserActionMessage(message);
    }
  }, [deleteUserMutation, deletingUser, usersQuery]);

  const handleConfirmDeleteAllUsers = useCallback(async () => {
    setUserActionMessage('');
    try {
      await deleteAllUsersMutation.mutateAsync();
      setIsDeleteAllModalOpen(false);
      setUserActionMessage('All non-admin users were deleted successfully.');
      await usersQuery.refetch();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete all users.';
      setUserActionMessage(message);
    }
  }, [deleteAllUsersMutation, usersQuery]);

  const userColumns = useMemo(
    () =>
      createUserColumns({
        onEditUser: handleEditUser,
        onDeleteUser: handleDeleteUser,
      }),
    [handleDeleteUser, handleEditUser]
  );

  const errorMessage = (usersQuery.error as Error | null)?.message ?? importErrorMessage;

  return (
    <>
      {usersQuery.isFetching && (
        <p className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-700">Loading users from /users...</p>
      )}
      {errorMessage && <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{errorMessage}</p>}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <Table<User>
          data={sortedUsers}
          columns={userColumns}
          topHeader={
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <div className="max-w-lg flex-1">
                <Input value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="Search" aria-label="Search users" />
              </div>
              {/* <button
                type="button"
                onClick={() => setIsDeleteAllModalOpen(true)}
                disabled={deleteAllUsersMutation.isPending || hasImportedUsers}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-500 disabled:cursor-not-allowed disabled:bg-red-300"
              >
                {deleteAllUsersMutation.isPending ? 'Deleting All...' : 'Delete All Users'}
              </button> */}
            </div>
          }
          loading={usersQuery.isFetching}
          enableSorting
          sorting={sorting}
          onSortingChange={setSorting}
          pagination={
            !hasImportedUsers
              ? {
                  pageIndex: usersPageIndex,
                  pageSize: usersPageSize,
                  totalCount: usersMeta?.total ?? sortedUsers.length,
                  totalPages: usersMeta?.totalPages,
                  onPageChange: setUsersPageIndex,
                  onPageSizeChange: setUsersPageSize,
                  pageSizeOptions: [10, 20, 40, 80],
                }
              : undefined
          }
        />
        {userActionMessage && (
          <p className="mt-4 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-700">{userActionMessage}</p>
        )}
      </div>

      <Modal
        isOpen={Boolean(editingUser)}
        onClose={handleCloseEditUserModal}
        title={editingUser ? `Edit User #${editingUser.id}` : 'Edit User'}
        footer={
          <div className="flex w-full justify-end gap-2 px-4 py-2">
            <button
              type="button"
              onClick={handleCloseEditUserModal}
              className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handleSaveUserEdit()}
              disabled={!isUserEditChanged || updateUserMutation.isPending}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:bg-indigo-300"
            >
              {updateUserMutation.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        }
      >
        {editingUser && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-slate-700">First Name</span>
              <input
                value={editUserFirstName}
                onChange={(event) => setEditUserFirstName(event.target.value)}
                className="rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-700 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-slate-700">Last Name</span>
              <input
                value={editUserLastName}
                onChange={(event) => setEditUserLastName(event.target.value)}
                className="rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-700 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-slate-700">Phone Number</span>
              <input
                value={editUserPhoneNumber}
                onChange={(event) => setEditUserPhoneNumber(event.target.value)}
                className="rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-700 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-slate-700">Email</span>
              <input value={editingUser.email} disabled className="rounded-md border border-slate-300 bg-slate-100 px-3 py-2 text-slate-600" />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-slate-700">Authorize.Net ID</span>
              <input
                value={editingUser.authorizeNetCustomerId}
                disabled
                className="rounded-md border border-slate-300 bg-slate-100 px-3 py-2 text-slate-600"
              />
            </label>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={Boolean(deletingUser)}
        onClose={() => setDeletingUser(null)}
        title="Delete user"
        subheading="This action cannot be undone."
        footer={
          <div className="flex w-full justify-end gap-2 px-4 py-2">
            <button
              type="button"
              onClick={() => setDeletingUser(null)}
              className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handleConfirmDeleteUser()}
              disabled={deleteUserMutation.isPending}
              className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-500 disabled:cursor-not-allowed disabled:bg-red-300"
            >
              {deleteUserMutation.isPending ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        }
      >
        {deletingUser && (
          <p className="text-sm text-slate-700">
            Are you sure you want to delete <span className="font-semibold">{`${deletingUser.firstName} ${deletingUser.lastName}`}</span>?
          </p>
        )}
      </Modal>

      <Modal
        isOpen={isDeleteAllModalOpen}
        onClose={() => setIsDeleteAllModalOpen(false)}
        title="Delete all users"
        subheading="This will delete all non-admin users and cannot be undone."
        footer={
          <div className="flex w-full justify-end gap-2 px-4 py-2">
            <button
              type="button"
              onClick={() => setIsDeleteAllModalOpen(false)}
              className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handleConfirmDeleteAllUsers()}
              disabled={deleteAllUsersMutation.isPending}
              className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-500 disabled:cursor-not-allowed disabled:bg-red-300"
            >
              {deleteAllUsersMutation.isPending ? 'Deleting...' : 'Delete All'}
            </button>
          </div>
        }
      >
        <p className="text-sm text-slate-700">Are you sure you want to delete all non-admin users?</p>
      </Modal>
    </>
  );
}

export default UsersTab;
